import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { ORDER_STATUSES, ORDER_STATUS_COLORS } from "../lib/ordersLib";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Search, Plus, FileDown, FileUp, FileSpreadsheet, ChevronLeft, ChevronRight, ArrowUpDown, Package, ClipboardCheck, Factory, CalendarDays } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-[#1B2A4A]" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold truncate">{label}</p>
        <p className="text-xl font-bold font-mono text-[#1B2A4A]">{value}</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

const COLS = [
  { key: "order_number", labelKey: "ord_order_number", sortable: true },
  { key: "account_name", labelKey: "ord_customer", sortable: true },
  { key: "po_number", labelKey: "ord_po", sortable: true },
  { key: "status", labelKey: "status", sortable: true },
  { key: "order_date", labelKey: "ord_date", sortable: true },
  { key: "requested_date", labelKey: "ord_requested", sortable: true },
  { key: "line_count", labelKey: "ord_lines", align: "right" },
  { key: "total", labelKey: "ord_total", sortable: true, align: "right" },
  { key: "source", labelKey: "ord_source" },
];

export default function Orders() {
  const { t, canWrite } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [accountId, setAccountId] = useState("");
  const [sort, setSort] = useState("order_date");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ account_id: "", po_number: "", requested_date: "" });
  const fileRef = useRef(null);
  const debounce = useRef(null);
  const [qDebounced, setQDebounced] = useState("");

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setQDebounced(q); setPage(1); }, 400);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: pageSize, sort, dir });
    if (qDebounced) params.set("q", qDebounced);
    if (status) params.set("status", status);
    if (accountId) params.set("account_id", accountId);
    api.get(`/orders?${params}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, pageSize, sort, dir, qDebounced, status, accountId]);

  useEffect(load, [load]);
  useEffect(() => {
    api.get("/orders/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data.sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  const toggleSort = (key) => {
    if (sort === key) setDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSort(key); setDir("desc"); }
    setPage(1);
  };

  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
    } catch { toast.error("Download error"); }
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (qDebounced) params.set("q", qDebounced);
    if (status) params.set("status", status);
    if (accountId) params.set("account_id", accountId);
    downloadFile(`/orders/export-excel?${params}`, "orders_export.xlsx");
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/orders/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} ${t("ord_import_ok")}`);
      if (r.data.errors?.length) toast.warning(r.data.errors.slice(0, 3).join("; "));
      load();
    } catch (err) { toast.error(errDetail(err)); }
    e.target.value = "";
  };

  const createOrder = async () => {
    if (!draft.account_id) { toast.error(t("ord_customer_required")); return; }
    try {
      const r = await api.post("/orders", draft);
      toast.success(`${t("ord_created")}: ${r.data.order_number}`);
      navigate(`/orders/${r.data.id}`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("orders")}</h1>
        {canWrite && (
          <Button data-testid="orders-new-btn" onClick={() => { setDraft({ account_id: "", po_number: "", requested_date: "" }); setCreateOpen(true); }}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
            <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("ord_new")}
          </Button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Package} label={t("ord_stats_total")} value={stats.total.toLocaleString("de-DE")} testid="orders-stat-total" />
          <StatCard icon={ClipboardCheck} label={t("ord_stats_open_conf")} value={stats.open_confirmations.toLocaleString("de-DE")} testid="orders-stat-openconf" />
          <StatCard icon={Factory} label={t("ord_stats_production")} value={(stats.by_status?.["In Production"]?.count || 0).toLocaleString("de-DE")} testid="orders-stat-production" />
          <StatCard icon={CalendarDays} label={t("ord_stats_month")} value={stats.month_count.toLocaleString("de-DE")} sub={fmtEur(stats.month_value)} testid="orders-stat-month" />
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input data-testid="orders-list-search" className="pl-8 h-9" placeholder={t("search_placeholder")}
              value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="orders-filter-status" className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700"
            value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">{`${t("status")}: ${t("all")}`}</option>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="orders-filter-account" className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700 max-w-[220px]"
            value={accountId} onChange={(e) => { setAccountId(e.target.value); setPage(1); }}>
            <option value="">{`${t("ord_customer")}: ${t("all")}`}</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <span className="text-xs text-gray-500 font-mono" data-testid="orders-total-count">{data.total.toLocaleString("de-DE")} {t("records")}</span>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="orders-excel-export-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" />{t("ord_excel_export")}
          </Button>
          {canWrite && (
            <>
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="orders-excel-import-btn">
                <FileUp className="h-3.5 w-3.5 mr-1" />{t("ord_excel_import")}
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadFile("/orders/template-excel", "orders_import_template.xlsx")} data-testid="orders-excel-template-btn">
                <FileSpreadsheet className="h-3.5 w-3.5 mr-1" />{t("ord_excel_template")}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importExcel} />
            </>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                {COLS.map((c) => (
                  <th key={c.key} className={`px-4 py-2.5 font-semibold ${c.align === "right" ? "text-right" : ""} ${c.sortable ? "cursor-pointer select-none hover:text-gray-800" : ""}`}
                    onClick={c.sortable ? () => toggleSort(c.key) : undefined} data-testid={`orders-sort-${c.key}`}>
                    <span className="inline-flex items-center gap-1">
                      {t(c.labelKey)}
                      {c.sortable && <ArrowUpDown className={`h-3 w-3 ${sort === c.key ? "text-[#F5A623]" : "text-gray-300"}`} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={loading ? "opacity-50" : ""}>
              {data.items.map((r) => (
                <tr key={r.id} data-testid="orders-row" onClick={() => navigate(`/orders/${r.id}`)}
                  className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono font-medium text-[#1B2A4A]">{r.order_number}</td>
                  <td className="px-4 py-2.5">{r.account_name}</td>
                  <td className="px-4 py-2.5 font-mono text-gray-500">{r.po_number || "–"}</td>
                  <td className="px-4 py-2.5"><Badge className={`${ORDER_STATUS_COLORS[r.status] || ""} hover:bg-inherit`}>{r.status}</Badge></td>
                  <td className="px-4 py-2.5">{fmtDate(r.order_date)}</td>
                  <td className="px-4 py-2.5">{fmtDate(r.requested_date)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{r.line_count}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{fmtEur(r.total, r.currency)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.source}</td>
                </tr>
              ))}
              {data.items.length === 0 && !loading && (
                <tr><td colSpan={COLS.length} className="px-4 py-12 text-center text-gray-400" data-testid="orders-empty-state">{t("empty_title")}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-100 text-sm">
          <select data-testid="orders-page-size" className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-600"
            value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
            {[25, 50, 100].map((n) => <option key={n} value={n}>{`${n} / ${t("ord_page")}`}</option>)}
          </select>
          <div className="flex-1" />
          <span className="text-xs text-gray-500" data-testid="orders-page-info">
            {t("ord_page")} {data.page || page} / {data.pages} · {data.total.toLocaleString("de-DE")} {t("records")}
          </span>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="orders-prev-page">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="orders-next-page">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("ord_new")}</DialogTitle>
            <DialogDescription>{t("ord_new_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("ord_customer")} *</Label>
              <select data-testid="order-create-account" className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm mt-1"
                value={draft.account_id} onChange={(e) => setDraft((d) => ({ ...d, account_id: e.target.value }))}>
                <option value="">–</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">{t("ord_po")}</Label>
              <Input data-testid="order-create-po" className="mt-1" value={draft.po_number}
                onChange={(e) => setDraft((d) => ({ ...d, po_number: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">{t("ord_requested")}</Label>
              <Input type="date" data-testid="order-create-requested" className="mt-1" value={draft.requested_date}
                onChange={(e) => setDraft((d) => ({ ...d, requested_date: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>{t("cancel")}</Button>
              <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold" onClick={createOrder} data-testid="order-create-submit">
                {t("create")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
